/**
 * Idle session timeout.
 *
 * Logs the user out after a period of inactivity (default 30 minutes) to
 * protect the application from unauthorised access on an unattended screen.
 *
 * - Activity (mouse, keyboard, scroll, touch) resets the timer.
 * - The last-activity time is mirrored to localStorage so closing the tab
 *   and returning after the window has elapsed also forces a re-login.
 * - On timeout it calls the provided `onTimeout` (the auth store's logout)
 *   and records a flag the login page can read to explain why.
 */

import { useEffect, useRef } from 'react';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const LAST_ACTIVITY_KEY = 'last_activity';
const TIMED_OUT_KEY = 'session_timed_out';
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

/**
 * @param enabled   Only arm the timer when a user is authenticated.
 * @param onTimeout Called when the idle window elapses (clears the session).
 */
export function useIdleTimeout(enabled: boolean, onTimeout: () => void) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastWriteRef = useRef<number>(0);

  useEffect(() => {
    if (!enabled) return;

    const fireTimeout = () => {
      try {
        localStorage.setItem(TIMED_OUT_KEY, '1');
        localStorage.removeItem(LAST_ACTIVITY_KEY);
      } catch {
        /* ignore storage errors */
      }
      onTimeout();
    };

    const armTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(fireTimeout, IDLE_TIMEOUT_MS);
    };

    const recordActivity = () => {
      const now = Date.now();
      // Throttle localStorage writes to once every 5s.
      if (now - lastWriteRef.current > 5000) {
        lastWriteRef.current = now;
        try {
          localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
        } catch {
          /* ignore */
        }
      }
      armTimer();
    };

    // If the stored last-activity is already older than the window
    // (e.g. the tab was closed for 40 minutes), time out immediately.
    const stored = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0);
    if (stored > 0 && Date.now() - stored >= IDLE_TIMEOUT_MS) {
      fireTimeout();
      return;
    }

    // Seed activity and start the timer.
    recordActivity();
    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, recordActivity, { passive: true })
    );

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, recordActivity));
    };
  }, [enabled, onTimeout]);
}

/** Read-and-clear the "you were logged out for inactivity" flag. */
export function consumeTimedOutFlag(): boolean {
  try {
    const flag = localStorage.getItem(TIMED_OUT_KEY) === '1';
    if (flag) localStorage.removeItem(TIMED_OUT_KEY);
    return flag;
  } catch {
    return false;
  }
}
